import {
  caluclateCHPercentage,
  caluclatePL,
  calculateCh,
  calculateSize,
  calcualteDeltaAdjustedShareCount,
} from "../../component/Methods/MonitorScreenFormulae.js";

import { useSelector } from "react-redux";
import { findName, calculateTdg } from "../../component/Methods/index.js";
import { formatTimeAgo } from "../../utils/helpers.js";
import { fetchDeltasForTickers } from "../../utils/polygon.js";

import {
  findLivePriceForTicker,
  getLivePriceTickerKey,
  normalizeLivePriceEntry,
} from "../../utils/livePriceTickerMatch";

const contractUnits = 100;
let fetched = "N/A";

// Helper function to safely call toFixed on potentially undefined values
function safeToFixed(value, decimals = 2) {
  if (value === undefined || value === null || isNaN(value)) {
    return "0." + "0".repeat(decimals);
  }
  return Number(value).toFixed(decimals);
}

// Helper function to safely parse float values
function safeParseFloat(value, defaultValue = 0) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

// Global cache for expensive operations with timestamps
const priceCache = new Map();
const deltaCache = new Map();
const strategyCache = new Map();
const fundCache = new Map();

// Cache expiration time (3 minutes)
const CACHE_EXPIRY_TIME = 3 * 60 * 1000;

// Memoized price search function
function searchPrice(ticker, marketDataObject) {
  const lookupTicker =
    typeof ticker === "string" ? { symbol: ticker } : ticker || {};

  // Validate inputs
  if (
    !lookupTicker.symbol ||
    !marketDataObject ||
    !Array.isArray(marketDataObject)
  ) {
    return [0, 0, 0.9, "N/A", false, "N/A"];
  }

  const cacheKey = `${getLivePriceTickerKey(normalizeLivePriceEntry(lookupTicker))}-${marketDataObject.length}`;
  
  // Check if cache exists and is still valid
  try {
    if (priceCache.has(cacheKey)) {
      const cachedData = priceCache.get(cacheKey);
      if (cachedData && cachedData.timestamp && Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
        return cachedData.data;
      }
      // Remove expired cache entry
      priceCache.delete(cacheKey);
    }
  } catch (error) {
    console.warn("Error accessing price cache:", error);
    // Clear potentially corrupted cache entry
    priceCache.delete(cacheKey);
  }

  if (marketDataObject.length > 1) {
    let matchingTicker = findLivePriceForTicker(
      marketDataObject,
      lookupTicker
    );

    let c = matchingTicker?.price === undefined ? 0 : matchingTicker?.price;
    let l = matchingTicker?.yestPrice === undefined ? 0 : matchingTicker?.yestPrice;
    let d = matchingTicker?.expiration === undefined ? fetched : matchingTicker?.expiration;

    let e, f;
    try {
      d = formatTimeAgo(d);
      e = d?.msg || "N/A";
      f = d?.valid || false;
    } catch (error) {
      console.warn("Error in formatTimeAgo:", error);
      e = "N/A";
      f = false;
    }

    if (c === 0 || l === 0) {
      f = false;
    }

    // Cache delta lookup
    const deltaKey = getLivePriceTickerKey(normalizeLivePriceEntry(lookupTicker));
    let delta = 0.9;
    
    // Check if delta cache exists and is still valid
    if (deltaCache.has(deltaKey)) {
      const cachedData = deltaCache.get(deltaKey);
      if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
        delta = cachedData.data;
      } else {
        // Remove expired cache entry
        deltaCache.delete(deltaKey);
      }
    }
    
    if (!deltaCache.has(deltaKey)) {
      try {
        const deltaObject = fetchDeltasForTickers();
        if (Array.isArray(deltaObject)) {
          let matchingDelta = deltaObject.filter(
            (item) => item && Object.keys(item)[0] === deltaKey
          );
          delta = Math.abs(safeParseFloat(matchingDelta[0]?.[deltaKey], 0.9)) || 0.9;
        }
      } catch (error) {
        console.warn("Error fetching deltas:", error);
        delta = 0.9; // Default delta value
      }
      deltaCache.set(deltaKey, { data: delta, timestamp: Date.now() });
    }

    const result = [c, l, delta, e, f, matchingTicker?.market];
    try {
      priceCache.set(cacheKey, { data: result, timestamp: Date.now() });
    } catch (error) {
      console.warn("Error setting price cache:", error);
    }
    return result;
  } else {
    return [0, 0, 0.9, "N/A", false, "N/A"];
  }
}

// Memoized strategy matching
function getMatchingStrategies(selectedStrategy, selectedSide, strategies) {
  const cacheKey = `${selectedStrategy}-${selectedSide}-${strategies?.length}`;
  
  // Check if cache exists and is still valid
  if (strategyCache.has(cacheKey)) {
    const cachedData = strategyCache.get(cacheKey);
    if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
      return cachedData.data;
    }
    // Remove expired cache entry
    strategyCache.delete(cacheKey);
  }

  let matchingStrategies = [];
  
  if (selectedSide === "Both" || selectedSide === undefined) {
    matchingStrategies = strategies?.filter(
      (strategy) =>
        strategy.name === selectedStrategy ||
        strategy.name.startsWith(`${selectedStrategy}_`)
    );
  } else if (selectedSide === "Long" || selectedSide === "Short") {
    matchingStrategies = strategies?.filter((strategy) =>
      strategy.name.includes(`${selectedStrategy}_${selectedSide}`)
    );
  }

  strategyCache.set(cacheKey, { data: matchingStrategies, timestamp: Date.now() });
  return matchingStrategies;
}

// Memoized fund AUM calculation
function getFundAUM(selectedFund, selectedStrategy, accountingData, strategies) {
  const cacheKey = `${selectedFund}-${selectedStrategy}-${accountingData?.length}`;
  
  // Check if cache exists and is still valid
  if (fundCache.has(cacheKey)) {
    const cachedData = fundCache.get(cacheKey);
    if (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME) {
      return cachedData.data;
    }
    // Remove expired cache entry
    fundCache.delete(cacheKey);
  }

  let totalAUM = 10;
  
  if (accountingData?.length > 0) {
    if (selectedFund) {
      let matchingObj = accountingData[0].funds.find(
        (obj) => obj.fund == selectedFund
      );

      if (matchingObj) {
        if (
          selectedStrategy !== undefined &&
          selectedStrategy !== "ALL" &&
          selectedStrategy !== ""
        ) {
          const matchingStrategies = getMatchingStrategies(selectedStrategy, "Both", strategies);
          const strategyAUMs = matchingObj?.strategyAUMs || {};

          totalAUM = matchingStrategies.reduce((sum, strategy) => {
            const strategyId = strategy.strategyId;
            return sum + (strategyAUMs[strategyId] || 0);
          }, 0);
        } else {
          totalAUM = matchingObj?.aum;
        }
      } else {
        totalAUM = accountingData[0]?.funds.reduce(
          (total, obj) => total + obj.aum,
          0
        );
      }
    } else {
      totalAUM = accountingData[0]?.funds.reduce(
        (total, obj) => total + obj.aum,
        0
      );
    }
  }

  fundCache.set(cacheKey, { data: totalAUM, timestamp: Date.now() });
  return totalAUM;
}

const MonitorScreenCalculation = ({
  selectedFund,
  selectedStrategy,
  selectedSide,
  lots,
  accountingData,
  funds,
  strategies,
  marketData,
  trades,
  isStrategist,
  markets
}) => {
  // Early return if no lots data
  if (!lots || lots.length === 0) {
    return [];
  }

  // Filter lots once at the beginning
  const filteredLots = lots?.filter((lot) => lot?.ticker?.investment == 4) || [];

  // Early return if no filtered lots
  if (filteredLots.length === 0) {
    return [];
  }

  const valuesForTable = [];
  var visitedTicker = [];

  // Get matching strategies and AUM efficiently
  const matchingStrategies = isStrategist 
    ? strategies?.filter(
        (strategy) =>
          strategy?.name === selectedStrategy ||
          strategy?.name?.startsWith(`${selectedStrategy}_`)
      ) || []
    : getMatchingStrategies(selectedStrategy, selectedSide, strategies);

  const totalAUM = isStrategist 
    ? (matchingStrategies.length > 0 ? matchingStrategies[0]?.aum : 10)
    : getFundAUM(selectedFund, selectedStrategy, accountingData, strategies);

  // Filter lots by fund and strategy once
  let processedLots = filteredLots;

  if (selectedFund) {
    processedLots = processedLots?.filter((obj) => {
      return obj?.fund == selectedFund;
    }) || [];
  }

  if (selectedStrategy) {
    const matchingStrategyIds = matchingStrategies?.map(
      (strategy) => strategy?.strategyId
    ) || [];

    processedLots = processedLots?.filter((obj) => matchingStrategyIds?.includes(obj?.strategy)) || [];
  }

  // Early return if no processed lots
  if (processedLots.length === 0) {
    return [];
  }

  // Pre-calculate trades data
  let calculatedData = [];
  if (trades && Array.isArray(trades)) {
    try {
      if (matchingStrategies.length > 0) {
        const str_id = matchingStrategies[0]?.strategyId;
        calculatedData = calculateTdg(trades, marketData, str_id);
      } else {
        calculatedData = calculateTdg(trades, marketData);
      }
    } catch (error) {
      console.warn("Error calculating TDG:", error);
      calculatedData = [];
    }
  }

  // Create a Map for faster trade lookups
  const tradeMap = new Map();
  calculatedData?.forEach(item => {
    if (item?.symbol) {
      tradeMap.set(item.symbol.toLowerCase(), item);
    }
  });

  // Process lots more efficiently with optimized loops
  for (var i = 0; i < processedLots?.length; i++) {
    const currentLot = processedLots[i];
    if (!currentLot || !currentLot.ticker || !currentLot.ticker.symbol) continue;
    
    if (!visitedTicker.includes(currentLot.ticker.symbol)) {
      if (currentLot.shareCount == 0) continue;

      visitedTicker.push(currentLot.ticker.symbol);

      let livePrices = searchPrice(currentLot.ticker, marketData);
      let tableFund = selectedFund || "All";
      let tableStrategyId = selectedStrategy ? currentLot.strategy : "All";
      let tableStrategyName = "All";
      if (selectedStrategy && currentLot.strategy) {
        try {
          tableStrategyName = findName(currentLot.strategy, strategies) || "All";
        } catch (error) {
          console.warn("Error finding strategy name:", error);
          tableStrategyName = "All";
        }
      }

      let marketName = "";
      const matchedMarket = markets?.find(
        (market) => market?.marketId == livePrices[5]
      );

      if (matchedMarket) {
        marketName = matchedMarket?.name || "";
      }
      if(marketName == "FMP"){
        marketName = "Primary"
      }

      var valuesForTableObject = {
        tickerSymbol: currentLot.ticker.stock 
            ? currentLot.ticker.symbol + "/" + currentLot.ticker.stock 
            : currentLot.ticker.symbol,
        tickerInvestment: currentLot.ticker.investment || 0,
        sharePrice: livePrices[0],
        last: livePrices[1],
        delta: livePrices[2],
        size: 0,
        equityShareCount: 0,
        deltaAdjustedShareCount: 0,
        impactOfOptionsContracts: 0,
        ch: 0,
        pL: 0,
        fund: tableFund,
        fundStrategyName: tableStrategyName,
        strategyName: tableStrategyId,
        dataSource: marketName,
        fetched: livePrices[3],
        valid: livePrices[4],
        fundName: "",
        custodian: "",
        shareCount: 0,
        Strategy: [],
        chPercetage: 0,
        profitLossWeek: 0,
        profitLossMonth: 0,
        profitLossQuarter: 0,
        profitLossYear: 0,
        cost: 0,
        tdg: 0,
      };

      // Optimized loop through all lots for the same ticker
      for (var j = 0; j < processedLots.length; j++) {
        const innerLot = processedLots[j];
        if (!innerLot || !innerLot.ticker || !innerLot.ticker.symbol) continue;
        
        if (innerLot.ticker.symbol === currentLot.ticker.symbol) {
          if (innerLot.ticker.investment === 9) {
            valuesForTableObject.impactOfOptionsContracts +=
              (innerLot.shareCount || 0) * contractUnits * valuesForTableObject.delta;
          }
          if (innerLot.ticker.investment === 4) {
            valuesForTableObject.equityShareCount += innerLot.shareCount || 0;
            valuesForTableObject.shareCount += innerLot.shareCount || 0;
            valuesForTableObject.cost += innerLot.cost || 0;
          }

          valuesForTableObject.profitLossWeek += innerLot.profitLossWeek || 0;
          valuesForTableObject.profitLossMonth += innerLot.profitLossMonth || 0;
          valuesForTableObject.profitLossQuarter += innerLot.profitLossQuarter || 0;
          valuesForTableObject.profitLossYear += innerLot.profitLossYear || 0;
          valuesForTableObject.custodian = innerLot?.account?.custodian || "";
        }
      }

      valuesForTableObject.deltaAdjustedShareCount =
        calcualteDeltaAdjustedShareCount(
          valuesForTableObject.equityShareCount,
          valuesForTableObject.impactOfOptionsContracts
        );

      valuesForTableObject.size = safeToFixed(
        calculateSize(
          valuesForTableObject.deltaAdjustedShareCount,
          valuesForTableObject.sharePrice,
          totalAUM
        ),
        2
      );

      valuesForTableObject.ch = calculateCh(
        valuesForTableObject.sharePrice,
        valuesForTableObject.last
      );

      valuesForTableObject.pL = safeToFixed(
        caluclatePL(
          valuesForTableObject.ch,
          valuesForTableObject.equityShareCount
        ),
        1
      );

      valuesForTableObject.chPercetage = safeToFixed(
        caluclateCHPercentage(
          valuesForTableObject.sharePrice,
          valuesForTableObject.last
        ),
        1
      );

      let matchingFund = funds?.find(
        (obj) => obj?.fundId === valuesForTableObject.fund
      );
      if (matchingFund) {
        valuesForTableObject.fundName = matchingFund?.abbreviation || "ALL";
      } else {
        valuesForTableObject.fundName = "ALL";
      }

      // Use Map for faster trade lookup
      const inTrade = tradeMap.get(valuesForTableObject.tickerSymbol.toLowerCase());

      if (inTrade) {
        valuesForTableObject.tdg = safeParseFloat(inTrade.PnL);

        const totalCost = valuesForTableObject.cost + safeParseFloat(inTrade.price) * inTrade.completedShares;
        const totalShareCount = valuesForTableObject.shareCount + inTrade.completedShares;
        valuesForTableObject.avgCost = Math.abs(totalCost) / Math.abs(totalShareCount) || 0;
      } else {
        valuesForTableObject.avgCost =  Math.abs(valuesForTableObject.cost) / Math.abs(valuesForTableObject.shareCount) || 0;
      }
      valuesForTable.push(valuesForTableObject);
    }
  }

  // Process new trades more efficiently
  if (trades && Array.isArray(calculatedData)) {
    const newTrades = calculatedData.filter(
      (secondItem) =>
        secondItem?.symbol &&
        !valuesForTable.some(
          (firstItem) => firstItem?.tickerSymbol === secondItem.symbol
        )
    );

    newTrades.forEach((item) => {
      let multiplier = 1;
      if (item.position == 1) multiplier = -1;
      const size = safeToFixed(
        calculateSize(
          item.completedShares * multiplier,
          item.currentMarketPrice,
          totalAUM
        ),
        2
      );

      const change = calculateCh(
        item.currentMarketPrice,
        safeParseFloat(item.price)
      );

      const profitLoss = safeToFixed(
        caluclatePL(change, item.completedShares),
        1
      );

      const profitLossPer = safeToFixed(
        caluclateCHPercentage(
          item.currentMarketPrice,
          safeParseFloat(item.price)
        ),
        1
      );

      const newPos = {
        size: size,
        sharePrice: item.currentMarketPrice,
        deltaAdjustedShareCount: item.completedShares * multiplier,
        ch: change,
        pL: profitLoss,
        avgCost: safeParseFloat(item.price),
        last: safeParseFloat(item.price),
        tickerSymbol: item.symbol,
        chPercentage: profitLossPer,
        fund: "ALL",
        dataSource: "Bloomberg",
        fetched: "Just Now",
        valid: true,
        tdg: safeParseFloat(item.PnL),
      };

      valuesForTable.push(newPos);
    });
  }

  return valuesForTable;
};

export default MonitorScreenCalculation;
